'use client'

import * as React from 'react'
import type { Matcher } from 'react-day-picker'

import { format } from 'date-fns'
import { ChevronDown } from 'lucide-react'

import { Button, ButtonProps } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'

import { cn } from '@/utils'

/**
 * DateMatcher type for disabling specific dates in the DatePicker
 * Re-export the Matcher type from react-day-picker
 */
export type DateMatcher = Matcher

export type DatePickerProps = DatePickerStandaloneProps

export type DatePickerStandaloneProps = {
  align?: 'center' | 'end' | 'start'
  className?: string
  'data-invalid'?: boolean | undefined
  /** Can be boolean to disable entire component, or DateMatcher (or array) to disable specific dates */
  disabled?: boolean | DateMatcher | DateMatcher[]
  endIcon?: React.ReactNode
  endMonth?: Date
  format?: (date: Date) => string
  locale?: string
  noChevron?: boolean
  onBlur?: () => void
  onUpdate?: (value: null | string) => void
  placeholder?: string
  size?: ButtonProps['size']
  startIcon?: React.ReactNode
  startMonth?: Date
  value?: null | string
}

export const DatePicker: React.FC<DatePickerProps> = ({
  align = 'start',
  className,
  'data-invalid': dataInvalid,
  disabled,
  endIcon,
  endMonth,
  format: formatFn,
  locale = 'en-US',
  noChevron = false,
  onBlur,
  onUpdate,
  placeholder,
  size,
  startIcon,
  startMonth,
  value
}) => {
  const resolvedPlaceholder = placeholder ?? 'Select date'
  const [open, setOpen] = React.useState(false)

  // Determine if the entire component is disabled (boolean true)
  const isComponentDisabled = disabled === true

  // Get disabled dates matcher for the calendar
  const disabledDates = React.useMemo<Matcher | Matcher[] | undefined>(() => {
    if (disabled === true || disabled === false || disabled === undefined) {
      return undefined
    }
    return disabled
  }, [disabled])

  const selectedDate = React.useMemo(() => {
    if (!value) return undefined
    try {
      const date = new Date(value)
      return isNaN(date.getTime()) ? undefined : date
    } catch {
      return undefined
    }
  }, [value])

  const displayValue = React.useMemo(() => {
    if (!selectedDate) return resolvedPlaceholder
    return formatFn
      ? formatFn(selectedDate)
      : format(selectedDate, 'MMM d, yyyy')
  }, [formatFn, resolvedPlaceholder, selectedDate])

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) {
      onUpdate?.(null)
      setOpen(false)
      return
    }
    // Format as YYYY-MM-DD
    const dateStr = format(date, 'yyyy-MM-dd')
    onUpdate?.(dateStr)
    setOpen(false)
  }

  return (
    <Popover onOpenChange={(o) => setOpen(o)} open={open}>
      <PopoverTrigger asChild>
        <Button
          className={cn(
            'items-between h-[42px] w-full max-w-full min-w-0 rounded-md',
            className
          )}
          data-invalid={dataInvalid || undefined}
          disabled={isComponentDisabled}
          onBlur={onBlur}
          size={size}
          variant="outline"
          wrapChildren={false}
        >
          <div className="flex w-full min-w-0 items-center gap-2">
            {startIcon ? startIcon : null}
            <span
              className={cn(
                'ms-1 min-w-0 truncate',
                !selectedDate && 'text-muted-foreground'
              )}
            >
              {displayValue}
            </span>
          </div>
          {!noChevron && <ChevronDown className="size-4 shrink-0 opacity-60" />}
          {endIcon ? endIcon : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-auto p-0">
        <Calendar
          captionLayout="dropdown"
          defaultMonth={selectedDate}
          disabled={disabledDates}
          endMonth={endMonth}
          locale={locale as never}
          mode="single"
          onSelect={handleDateSelect}
          selected={selectedDate}
          startMonth={startMonth}
        />
      </PopoverContent>
    </Popover>
  )
}
